import type {
  NavigationGuardReturn,
  RouteLocationNormalized,
} from "vue-router";
import { pathSegmentToSection } from "@/views/anchor-plan/workspace/shared/projectPath.js";

function internalWorkspaceSection(section: string | undefined): string {
  if (section === undefined || section === "") return "overview";
  const mapped = pathSegmentToSection(section);
  return mapped || section;
}

/** Прибирає `plan` із query там, де це не сторінки аналітики плану. */
export function sanitizeAnchorPlanProjectQuery(
  workspaceSection: string,
  query: RouteLocationNormalized["query"],
): Record<string, string | string[]> {
  const q: Record<string, string | string[]> = {};
  for (const [key, raw] of Object.entries(query)) {
    if (raw === null || raw === undefined) continue;
    q[key] = raw as string | string[];
  }
  const needsPlanQuery = ["analyze", "plan", "purchases"].includes(
    workspaceSection,
  );
  if (!needsPlanQuery) {
    delete q.plan;
  }
  return q;
}

export function planQueryString(to: RouteLocationNormalized): string {
  const raw = to.query.plan;
  if (typeof raw === "string") return raw.trim();
  if (Array.isArray(raw)) return String(raw[0] ?? "").trim();
  return "";
}

export function anchorPlanProjectWorkspaceBeforeEnter(
  to: RouteLocationNormalized,
): NavigationGuardReturn {
  const projectId = String(to.params.projectId);
  const secRaw = to.params.section as string | undefined;

  if (secRaw === "history") {
    return {
      name: "anchor-plan-project",
      params: { projectId, section: "plans" },
      query: sanitizeAnchorPlanProjectQuery(
        internalWorkspaceSection("plans"),
        to.query,
      ),
      hash: to.hash,
      replace: true,
    };
  }

  if (secRaw === undefined || secRaw === "") {
    return {
      name: "anchor-plan-project",
      params: { projectId, section: "overview" },
      query: sanitizeAnchorPlanProjectQuery("overview", to.query),
      hash: to.hash,
      replace: true,
    };
  }

  if (secRaw === "settings") {
    return {
      name: "anchor-plan-project-settings",
      params: { projectId, settingsTab: "budget" },
      query: sanitizeAnchorPlanProjectQuery("settings", to.query),
      hash: to.hash,
      replace: true,
    };
  }

  const internal = internalWorkspaceSection(secRaw);
  const planStr = planQueryString(to);

  if (["analyze", "plan", "purchases"].includes(internal)) {
    if (!planStr) {
      return {
        name: "anchor-plan-project",
        params: { projectId, section: "plans" },
        query: sanitizeAnchorPlanProjectQuery("dashboard", to.query),
        hash: to.hash,
        replace: true,
      };
    }
  }

  if (to.query.plan && !["analyze", "plan", "purchases"].includes(internal)) {
    return {
      name: "anchor-plan-project",
      params: to.params,
      query: sanitizeAnchorPlanProjectQuery(internal, to.query),
      hash: to.hash,
      replace: true,
    };
  }

  return true;
}

export function anchorPlanSettingsBeforeEnter(
  to: RouteLocationNormalized,
): NavigationGuardReturn {
  if (to.query.plan) {
    return {
      name: "anchor-plan-project-settings",
      params: to.params,
      query: sanitizeAnchorPlanProjectQuery("settings", to.query),
      hash: to.hash,
      replace: true,
    };
  }

  const tabRaw = String(to.params.settingsTab || "budget");
  if (!/^[\w-]+$/.test(tabRaw)) {
    return {
      name: "anchor-plan-project-settings",
      params: { projectId: String(to.params.projectId), settingsTab: "budget" },
      query: sanitizeAnchorPlanProjectQuery("settings", to.query),
      hash: to.hash,
      replace: true,
    };
  }
  return true;
}
